import {
  formatDate,
  formatEuro,
  selfDisclosureMaritalStatusLabels,
  type SelfDisclosureMaritalStatus,
} from '@brimo/shared';
import { Checkbox, Eyebrow, TextField } from '../../../ds/components';
import { SignatureCanvas } from '../../reservations/SignatureCanvas';
import { LegalLink } from '../../reservations/public/PublicFrame';
import { amountCents, type FieldErrors, type FormState } from './form';
import styles from './SelfDisclosurePublicPage.module.css';

export interface ConsentsState {
  completeness: boolean;
  sharing: boolean;
  propertyInfo: boolean;
}

export interface SignatureStepProps {
  form: FormState;
  consents: ConsentsState;
  onConsentsChange: (patch: Partial<ConsentsState>) => void;
  onSignatureChange: (dataUrl: string | null) => void;
  place: string;
  onPlaceChange: (place: string) => void;
  errors: FieldErrors;
  privacyUrl: string | null;
  onEdit: () => void;
}

function euro(text: string): string {
  const cents = amountCents(text);
  return cents === null || cents === undefined ? '—' : formatEuro(cents);
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.summaryRow}>
      <span className={styles.summaryLabel}>{label}</span>
      <span className={styles.summaryValue}>{value}</span>
    </div>
  );
}

/** Step 5 „Prüfen und unterschreiben" (Selbstauskunft 05): summary, the three consents, signature and place. */
export function SignatureStep({
  form,
  consents,
  onConsentsChange,
  onSignatureChange,
  place,
  onPlaceChange,
  errors,
  privacyUrl,
  onEdit,
}: SignatureStepProps) {
  const person = form.person;
  const maritalStatus =
    person.maritalStatus === ''
      ? null
      : (selfDisclosureMaritalStatusLabels[person.maritalStatus as SelfDisclosureMaritalStatus] ??
        person.maritalStatus);
  const children = person.children.trim() === '' ? null : Number(person.children);
  const childrenText =
    children === null
      ? null
      : children === 0
        ? 'keine Kinder'
        : children === 1
          ? '1 Kind'
          : `${children} Kinder`;
  const contract =
    form.income.contract === 'fixed_term'
      ? `befristet${form.income.fixedTermUntil ? ` bis ${form.income.fixedTermUntil}` : ''}`
      : form.income.contract === 'permanent'
        ? 'unbefristet'
        : '—';

  return (
    <>
      <div className={styles.card}>
        <div className={styles.summaryGrid}>
          <div>
            <Eyebrow as="div" className={styles.groupLabel}>
              Person
            </Eyebrow>
            <Row label="Name" value={`${person.firstName} ${person.lastName}`.trim() || '—'} />
            <Row label="Geburtsdatum" value={person.bornOn ? formatDate(person.bornOn) : '—'} />
            <Row
              label="Familienstand"
              value={[maritalStatus, childrenText].filter(Boolean).join(', ') || '—'}
            />
          </div>
          <div>
            <Eyebrow as="div" className={styles.groupLabel}>
              Einkommen
            </Eyebrow>
            <Row label="Netto monatlich" value={euro(form.income.netIncome)} />
            <Row label="Weitere Einnahmen" value={euro(form.income.otherIncome)} />
            <Row label="Arbeitsvertrag" value={contract} />
          </div>
          <div className={styles.summaryGroupBelow}>
            <Eyebrow as="div" className={styles.groupLabel}>
              Ausgaben
            </Eyebrow>
            <Row label="Warmmiete" value={euro(form.expenses.grossRent)} />
            <Row label="Leasing" value={euro(form.expenses.loans)} />
          </div>
          <div className={styles.summaryGroupBelow}>
            <Eyebrow as="div" className={styles.groupLabel}>
              Vermögen
            </Eyebrow>
            <Row label="Eigenkapital" value={euro(form.assets.equity)} />
            <Row label="Wertpapiere" value={euro(form.assets.securities)} />
          </div>
        </div>
        <div className={styles.summaryEdit}>
          <button type="button" className={styles.textLink} onClick={onEdit}>
            Angaben bearbeiten
          </button>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.consentsTitle}>Einwilligungen</div>
        <div className={styles.consentList}>
          <div className={styles.consentRow}>
            <Checkbox
              label="Ich versichere, dass meine Angaben vollständig und richtig sind. *"
              checked={consents.completeness}
              onChange={(event) => onConsentsChange({ completeness: event.target.checked })}
            />
            {errors['consentCompleteness'] ? (
              <span className={styles.fieldError} role="alert">
                {errors['consentCompleteness']}
              </span>
            ) : null}
          </div>
          <div className={styles.consentRow}>
            <label className={styles.consentLabel}>
              <input
                type="checkbox"
                className={styles.consentInput}
                checked={consents.sharing}
                onChange={(event) => onConsentsChange({ sharing: event.target.checked })}
              />
              <span className={styles.consentBox} aria-hidden="true" />
              <span className={styles.consentText}>
                Ich willige ein, dass meine Angaben an den zugewiesenen Finanzierungspartner und an Banken zur
                Prüfung einer Finanzierung weitergegeben werden.{' '}
                {privacyUrl ? (
                  <LegalLink url={privacyUrl} title="Datenschutzhinweise" className={styles.consentLink} />
                ) : null}
                {privacyUrl ? ' ' : ''}
                <span className={styles.required}>*</span>
              </span>
            </label>
            {errors['consentSharing'] ? (
              <span className={styles.fieldError} role="alert">
                {errors['consentSharing']}
              </span>
            ) : null}
          </div>
          <div className={styles.consentRow}>
            <Checkbox
              label="Ich möchte über passende weitere Objekte informiert werden."
              checked={consents.propertyInfo}
              onChange={(event) => onConsentsChange({ propertyInfo: event.target.checked })}
            />
          </div>
        </div>
        <div className={styles.signatureLabel}>
          Unterschrift <span className={styles.required}>*</span>
        </div>
        <SignatureCanvas
          label="Unterschrift"
          onChange={onSignatureChange}
          error={errors['signaturePng']}
          hint={`${place.trim() || 'Ort'}, ${formatDate(new Date())} · mit Finger oder Maus unterschreiben`}
        />
        <TextField
          label="Ort"
          required
          value={place}
          error={errors['place']}
          autoComplete="address-level2"
          className={styles.placeField}
          onChange={(event) => onPlaceChange(event.target.value)}
        />
      </div>
    </>
  );
}
